'use es6';

import { setupFilterPlaceholderResolver } from 'crm_data/filters/FilterPlaceholderResolver';
import { connectPromiseSingle } from 'crm_data/flux/connectPromiseSingle';
import CurrentOwnerIdStore from 'crm_data/owners/CurrentOwnerIdStore';
import CrmLogger from 'customer-data-tracking/loggers/CrmLogger';
import { navigate } from 'flux-navigation-utils/actions/NavigationActions';
import userInfo from 'hub-http/userInfo';
import dispatcher from 'dispatcher/dispatcher';
import { DispatcherInstance } from 'general-store';
import { setupData } from './legacy/setup/setupData';
import { setupJitaContainer } from './legacy/setup/setupJitaContainer';
import { setupScopesContainer } from './legacy/setup/setupScopesContainer';
import AppContainer from './new/AppContainer';
import I18nContext from './new/context/I18nContext';
import { TasksReduxContext } from './new/context/Redux';
import * as UsageLoggerRequiredData from './usage/UsageLoggerRequiredData';
import { rethrowError } from 'UIComponents/core/PromiseHandlers';
import UIApplication from 'UIComponents/app/UIApplication';

// I18n
import createStandardI18nProvider from 'I18n/init/providers/createStandardI18nProvider';
import lang from 'i2l?query=sporks!../lang/en.lyaml';
import registerAllData from 'I18n/init/register/sync/registerAllData';
import { initAppTranslations } from 'I18n/init/setup/AppInit';
// These need to be imported before we configure the Redux store as that setup relies on I18n
import 'I18n/init/data/baseLocales';
import 'I18n/init/data/allTimezones';

// redux
import { Provider } from 'react-redux';
import { store } from './new/redux/store';
import { initAuth } from './new/redux/actions/auth';
import { initUserSettings, initLocalStorageUserSettings } from './new/redux/actions/userSettings';
import { TASKS_USER_SETTINGS, TASKS_LOCAL_STORAGE_USER_SETTINGS } from './new/constants/userSettingsKeys';
import { navigateToURL } from './new/utils/links';
import { getUserSettingsEarlyRequesterData } from './new/earlyRequester/getUserSettingsEarlyRequesterData';
import { renderWithNavCopilot } from 'quartz-core/client-with-copilot';
import { jsx as _jsx } from "react/jsx-runtime";
export class App {
  legacyRouterInitialization() {
    // initialize NavigationStore, since various legacy code depends on the
    // store having values for the path and query params. those modules should
    // eventually be migrated to use react router's hooks
    navigate();
  }
  renderApplication(I18nProvider) {
    this.legacyRouterInitialization();
    const isOnOldRoute = window.location.pathname.includes('/contacts/');

    // if they should be on the new app and have the /contacts basename, update it to /tasks
    if (isOnOldRoute) {
      const newPathname = window.location.pathname.replace('/contacts/', '/tasks/');
      const searchString = window.location.search;
      // this may still have old routes but we can let the new router handle redirecting those
      navigateToURL(`${newPathname}${searchString}`);
      return;
    }
    renderWithNavCopilot( /*#__PURE__*/_jsx(UIApplication, {
      name: "crm-tasks-ui",
      children: /*#__PURE__*/_jsx(Provider, {
        context: TasksReduxContext,
        store: store,
        children: /*#__PURE__*/_jsx(I18nContext.Provider, {
          value: I18nProvider,
          children: /*#__PURE__*/_jsx(AppContainer, {})
        })
      })
    }), {
      layoutType: 'sidebar'
    });
  }
  setupFirstLegacyContainers(auth) {
    setupData(auth);
    setupScopesContainer(auth);
    CrmLogger.init(UsageLoggerRequiredData.get());
  }
  setupSecondLegacyContainers(auth, ownerId) {
    setupJitaContainer(auth, ownerId);
  }
  setupRedux(auth, ownerId) {
    const {
      user,
      gates,
      portal
    } = auth;
    store.dispatch(initAuth({
      user,
      gates,
      portal,
      ownerId
    }));
    store.dispatch(initLocalStorageUserSettings({
      settingKeys: TASKS_LOCAL_STORAGE_USER_SETTINGS
    }));
    const userSettingsEarlyRequestData = getUserSettingsEarlyRequesterData();
    store.dispatch(initUserSettings({
      userSettingsKeys: TASKS_USER_SETTINGS,
      earlyRequestData: userSettingsEarlyRequestData
    }));
  }
  start() {
    const I18nProvider = createStandardI18nProvider();
    const langAvailable = initAppTranslations([I18nProvider.register(lang), registerAllData(I18nProvider)]);
    const fetchCurrentOwnerId = connectPromiseSingle(CurrentOwnerIdStore);
    const ownersPromise = fetchCurrentOwnerId();
    if (window.performance && window.performance.mark) {
      window.performance.mark('mark_setup_loaded');
    }

    // Legacy generalstore setup
    DispatcherInstance.set(dispatcher);
    userInfo().then(auth => {
      if (window.performance && window.performance.mark) {
        window.performance.mark('mark_setup_fetched');
      }
      const {
        portal,
        user
      } = auth;
      I18nProvider.setLocale({
        locale: user.locale,
        langEnabled: user.lang_enabled,
        timezone: portal.timezone
      });
      this.setupFirstLegacyContainers(auth);
      return Promise.all([langAvailable, ownersPromise]).then(([__, ownerId]) => {
        setupFilterPlaceholderResolver(ownerId);
        this.setupSecondLegacyContainers(auth, ownerId);
        this.setupRedux(auth, ownerId);
        this.renderApplication(I18nProvider);
      });
    }).catch(rethrowError);
  }
}